<template>
	<div class="name-input__container name-input__container--flex">
		<label :for="`${column.name}-name`">Column Name</label>
		<InputText
			:id="`${column.name}-name`"
			ref="nameInput"
			v-model="nameValue"
			autofocus
			class="name-input__input"
			pattern="[a-z]+(?:_[a-z]+)*"
			placeholder="column_name (singular)"
			required
			title="Unique column name in snake_case"
			@blur="((nameValue = anyToSnakeCase(nameValue)), setName(nameValue ?? ''))"
			@input="clearValidationErrors()"
			@keydown.enter="((nameValue = anyToSnakeCase(nameValue)), setName(nameValue ?? ''))" />
	</div>
</template>

<script setup lang="ts">
import InputText from 'primevue/inputtext'
import { ref, watch, type ComponentPublicInstance } from 'vue'
import { anyToSnakeCase } from '@codecannon/schemas/utils/strings'
import type { EnrichedColumn } from '@codecannon/schemas/schemas/EnrichedColumn'
import type { EnrichedModule } from '@codecannon/schemas/schemas/EnrichedModule'

const props = defineProps<{
	column: EnrichedColumn
	module: EnrichedModule
}>()

const nameInput = ref<ComponentPublicInstance<typeof InputText> | null>(null)
const nameValue = ref<string>('')

watch(
	() => props.column,
	() => {
		nameValue.value = props.column.name
	},
	{ immediate: true },
)

function clearValidationErrors() {
	if (nameInput.value) {
		nameInput.value.$el.setCustomValidity('')
	}
}

function validateName(name: string) {
	if (
		nameInput.value &&
		name !== props.column.name &&
		Object.keys(props.module.model.columns).includes(name)
	) {
		nameInput.value.$el.setCustomValidity('Column with this name already exists')
		nameInput.value.$el.reportValidity()
		return false
	}
	if (
		nameInput.value &&
		name !== props.column.name &&
		Object.keys(props.module.model.relations).includes(name)
	) {
		nameInput.value.$el.setCustomValidity('Relation with this name already exists')
		nameInput.value.$el.reportValidity()
		return false
	}
	if (nameInput.value && (anyToSnakeCase(name) === 'id' || anyToSnakeCase(name).endsWith('_id'))) {
		nameInput.value.$el.setCustomValidity(
			"The 'id' column and columns ending with '_id' are reserved. Please choose a different column name.",
		)
		nameInput.value.$el.reportValidity()
		return false
	}
	return true
}

function setName(name: string) {
	if (!validateName(name)) return
	props.column.setNames(name)
}
</script>

<style lang="scss" scoped>
.name-input__container {
	display: flex;
	flex-direction: column;
	gap: 5px;
	margin-bottom: 16px;

	&:last-of-type {
		margin-bottom: 0;
	}

	&--flex {
		flex: 1;
	}

	.name-input__input {
		width: 100%;
	}
}
</style>
